const WHITE = { r: 255, g: 255, b: 255 }
const BLACK = { r: 0, g: 0, b: 0 }

export const DEFAULT_PRIMARY_COLOR = '#2563eb'
export const DEFAULT_SECONDARY_COLOR = '#16a34a'

const DEFAULT_RGB_VARS = {
  primary: {
    50: '239 246 255',
    100: '219 234 254',
    200: '191 219 254',
    300: '147 197 253',
    400: '96 165 250',
    500: '59 130 246',
    600: '37 99 235',
    700: '29 78 216',
    800: '30 64 175',
    900: '30 58 138',
  },
  secondary: {
    50: '240 253 244',
    100: '220 252 231',
    200: '187 247 208',
    300: '134 239 172',
    400: '74 222 128',
    500: '34 197 94',
    600: '22 163 74',
    700: '21 128 61',
    800: '22 101 52',
    900: '20 83 45',
  },
}

const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)))

const toRgbTuple = ({ r, g, b }) => `${clamp(r)} ${clamp(g)} ${clamp(b)}`

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback

  let next = value.trim()
  if (!next) return fallback
  if (!next.startsWith('#')) next = `#${next}`

  if (/^#[0-9a-fA-F]{3}$/.test(next)) {
    return `#${next[1]}${next[1]}${next[2]}${next[2]}${next[3]}${next[3]}`
  }

  if (/^#[0-9a-fA-F]{6}$/.test(next)) {
    return next.toLowerCase()
  }

  return fallback
}

const hexToRgb = (hex) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
})

const mixRgb = (source, target, weight) => ({
  r: source.r + (target.r - source.r) * weight,
  g: source.g + (target.g - source.g) * weight,
  b: source.b + (target.b - source.b) * weight,
})

const createPalette = (hex) => {
  const base = hexToRgb(hex)

  return {
    50: toRgbTuple(mixRgb(base, WHITE, 0.94)),
    100: toRgbTuple(mixRgb(base, WHITE, 0.86)),
    200: toRgbTuple(mixRgb(base, WHITE, 0.7)),
    300: toRgbTuple(mixRgb(base, WHITE, 0.5)),
    400: toRgbTuple(mixRgb(base, WHITE, 0.24)),
    500: toRgbTuple(mixRgb(base, WHITE, 0.08)),
    600: toRgbTuple(base),
    700: toRgbTuple(mixRgb(base, BLACK, 0.12)),
    800: toRgbTuple(mixRgb(base, BLACK, 0.24)),
    900: toRgbTuple(mixRgb(base, BLACK, 0.36)),
  }
}

export const buildThemeColorVars = (theme = {}) => {
  const primaryHex = normalizeHexColor(theme.primaryColor, DEFAULT_PRIMARY_COLOR)
  const secondaryHex = normalizeHexColor(theme.secondaryColor, DEFAULT_SECONDARY_COLOR)

  return {
    primaryHex,
    secondaryHex,
    primary: createPalette(primaryHex),
    secondary: createPalette(secondaryHex),
  }
}

export const applySchoolTheme = (theme = null) => {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const palette = theme
    ? buildThemeColorVars(theme)
    : {
        primaryHex: DEFAULT_PRIMARY_COLOR,
        secondaryHex: DEFAULT_SECONDARY_COLOR,
        primary: DEFAULT_RGB_VARS.primary,
        secondary: DEFAULT_RGB_VARS.secondary,
      }

  root.style.setProperty('--app-primary-hex', palette.primaryHex)
  root.style.setProperty('--app-secondary-hex', palette.secondaryHex)

  Object.entries(palette.primary).forEach(([step, rgb]) => {
    root.style.setProperty(`--color-primary-${step}`, rgb)
  })

  Object.entries(palette.secondary).forEach(([step, rgb]) => {
    root.style.setProperty(`--color-secondary-${step}`, rgb)
  })

  if (theme?.darkMode) {
    root.dataset.themeMode = 'dark'
  } else {
    delete root.dataset.themeMode
  }
}
